/**
 * Minecraft-style player model — head/body/arms/legs boxes with
 * procedurally drawn pixel-art skin textures (no image files).
 * Arms and legs pivot at the shoulders/hips for the walk animation.
 */

import * as THREE from "three";

export type PlayerModelColors = {
  skin: string;
  hair: string;
  shirt: string;
  pants: string;
  shoes: string;
};

export const DEFAULT_COLORS: PlayerModelColors = {
  skin: "#e0b088",
  hair: "#4a3220",
  shirt: "#2e8b57",
  pants: "#3a5a9a",
  shoes: "#3a3a3a",
};

export type PlayerModel = {
  group: THREE.Group;
  /** Advance walk/swing animation. speed in blocks/s; dt in seconds. */
  update: (dt: number, speed: number, swimming?: boolean) => void;
  /** Trigger a one-shot right-arm attack swing. */
  swingArm: () => void;
  dispose: () => void;
};

// Model dimensions (world units; total height ≈ 1.8)
const HEAD = 0.5;
const BODY_W = 0.5;
const BODY_H = 0.7;
const BODY_D = 0.25;
const LIMB_W = 0.22;
const LIMB_H = 0.65;
const LEG_TOP = LIMB_H; // hip height
const SHOULDER_Y = LEG_TOP + BODY_H;

/** Shade a hex color by a factor (pixel-art style face shading). */
function shade(hex: string, factor: number): string {
  const n = parseInt(hex.slice(1), 16);
  const r = Math.min(255, Math.round(((n >> 16) & 0xff) * factor));
  const g = Math.min(255, Math.round(((n >> 8) & 0xff) * factor));
  const b = Math.min(255, Math.round((n & 0xff) * factor));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

/**
 * Draws an 8x8 pixel-art tile with per-pixel noise for texture,
 * returned as a THREE texture (nearest filter = crisp pixels).
 */
function pixelTile(
  base: string,
  draw?: (ctx: CanvasRenderingContext2D) => void
): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = 8;
  canvas.height = 8;
  const ctx = canvas.getContext("2d")!;

  for (let y = 0; y < 8; y++) {
    for (let x = 0; x < 8; x++) {
      const noise = 0.92 + ((x * 7 + y * 13) % 5) * 0.035;
      ctx.fillStyle = shade(base, noise);
      ctx.fillRect(x, y, 1, 1);
    }
  }
  draw?.(ctx);

  const texture = new THREE.CanvasTexture(canvas);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function mat(texture: THREE.CanvasTexture): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({ map: texture });
}

function buildHead(colors: PlayerModelColors): THREE.Mesh {
  const face = pixelTile(colors.skin, (ctx) => {
    // Hair fringe
    ctx.fillStyle = colors.hair;
    ctx.fillRect(0, 0, 8, 2);
    // Eyes (white + pupil)
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(1, 3, 2, 1);
    ctx.fillRect(5, 3, 2, 1);
    ctx.fillStyle = "#3a2a6a";
    ctx.fillRect(2, 3, 1, 1);
    ctx.fillRect(5, 3, 1, 1);
    // Mouth
    ctx.fillStyle = shade(colors.skin, 0.65);
    ctx.fillRect(3, 6, 2, 1);
  });
  const side = pixelTile(colors.skin, (ctx) => {
    ctx.fillStyle = colors.hair;
    ctx.fillRect(0, 0, 8, 2);
  });
  const top = pixelTile(colors.hair);
  const bottom = pixelTile(shade(colors.skin, 0.8));

  // Box face order: +x, -x, +y, -y, +z, -z — model faces -z
  const materials = [
    mat(side), mat(side), mat(top), mat(bottom), mat(side), mat(face),
  ];
  const head = new THREE.Mesh(new THREE.BoxGeometry(HEAD, HEAD, HEAD), materials);
  head.position.y = SHOULDER_Y + HEAD / 2;
  return head;
}

function buildBody(colors: PlayerModelColors): THREE.Mesh {
  const front = pixelTile(colors.shirt, (ctx) => {
    // Collar + button line
    ctx.fillStyle = shade(colors.shirt, 0.75);
    ctx.fillRect(3, 0, 2, 1);
    ctx.fillRect(4, 1, 1, 6);
  });
  const back = pixelTile(colors.shirt);
  const side = pixelTile(shade(colors.shirt, 0.85));
  const cap = pixelTile(shade(colors.shirt, 0.9));

  const materials = [
    mat(side), mat(side), mat(cap), mat(cap), mat(back), mat(front),
  ];
  const body = new THREE.Mesh(new THREE.BoxGeometry(BODY_W, BODY_H, BODY_D), materials);
  body.position.y = LEG_TOP + BODY_H / 2;
  return body;
}

/** Limb pivoting at its top (shoulder/hip). */
function buildLimb(topColor: string, bottomColor: string, bottomRows: number): THREE.Group {
  const texture = (base: string) =>
    pixelTile(base, (ctx) => {
      ctx.fillStyle = bottomColor;
      ctx.fillRect(0, 8 - bottomRows, 8, bottomRows);
    });

  const materials = [
    mat(texture(topColor)), mat(texture(topColor)),
    mat(pixelTile(topColor)), mat(pixelTile(bottomColor)),
    mat(texture(topColor)), mat(texture(topColor)),
  ];
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(LIMB_W, LIMB_H, LIMB_W), materials);
  mesh.position.y = -LIMB_H / 2; // hang below the pivot

  const pivot = new THREE.Group();
  pivot.add(mesh);
  return pivot;
}

export function createPlayerModel(
  colors: PlayerModelColors = DEFAULT_COLORS
): PlayerModel {
  const group = new THREE.Group();
  // YXZ so the caller's yaw (rotation.y) and our swim tilt (rotation.x)
  // compose correctly (yaw first, then lean forward relative to facing).
  group.rotation.order = "YXZ";

  const head = buildHead(colors);
  const body = buildBody(colors);
  group.add(head, body);

  // Arms: sleeve on top, skin hand at the bottom
  const leftArm = buildLimb(colors.shirt, colors.skin, 3);
  leftArm.position.set(-(BODY_W / 2 + LIMB_W / 2), SHOULDER_Y, 0);
  const rightArm = buildLimb(colors.shirt, colors.skin, 3);
  rightArm.position.set(BODY_W / 2 + LIMB_W / 2, SHOULDER_Y, 0);

  // Legs: pants with shoes at the bottom
  const leftLeg = buildLimb(colors.pants, colors.shoes, 2);
  leftLeg.position.set(-LIMB_W / 2 - 0.02, LEG_TOP, 0);
  const rightLeg = buildLimb(colors.pants, colors.shoes, 2);
  rightLeg.position.set(LIMB_W / 2 + 0.02, LEG_TOP, 0);

  group.add(leftArm, rightArm, leftLeg, rightLeg);

  let walkPhase = 0;
  let swingTimer = 0;
  let swimTilt = 0;
  const SWING_DURATION = 0.3;
  const SWIM_ANGLE = 1.35; // ~77° forward lean while swimming

  function update(dt: number, speed: number, swimming = false) {
    // Lean forward into a swimming pose in water (no more upright sinking)
    const targetTilt = swimming ? SWIM_ANGLE : 0;
    swimTilt += (targetTilt - swimTilt) * Math.min(1, dt * 8);
    group.rotation.x = swimTilt;

    if (swimming) {
      // Breaststroke-ish: arms reach forward together, legs kick
      walkPhase += dt * 4;
      const stroke = Math.sin(walkPhase);
      leftArm.rotation.x = -2.2 + stroke * 0.5;
      rightArm.rotation.x = -2.2 - stroke * 0.5;
      leftLeg.rotation.x = stroke * 0.4;
      rightLeg.rotation.x = -stroke * 0.4;
      head.rotation.y = 0;
      return;
    }

    // Walk cycle speed scales with movement speed
    if (speed > 0.15) {
      walkPhase += dt * Math.min(speed, 8) * 2.2;
      const swing = Math.sin(walkPhase) * Math.min(speed / 4.3, 1) * 0.7;
      leftArm.rotation.x = swing;
      rightArm.rotation.x = -swing;
      leftLeg.rotation.x = -swing;
      rightLeg.rotation.x = swing;
    } else {
      // Ease limbs back to rest
      const ease = Math.min(1, dt * 10);
      leftArm.rotation.x *= 1 - ease;
      rightArm.rotation.x *= 1 - ease;
      leftLeg.rotation.x *= 1 - ease;
      rightLeg.rotation.x *= 1 - ease;
    }

    // One-shot attack swing overrides the right arm
    if (swingTimer > 0) {
      swingTimer = Math.max(0, swingTimer - dt);
      const t = 1 - swingTimer / SWING_DURATION;
      rightArm.rotation.x = -Math.sin(t * Math.PI) * 1.8;
    }

    // Subtle idle head bob
    head.rotation.y = Math.sin(walkPhase * 0.1) * 0.03;
  }

  function swingArm() {
    swingTimer = SWING_DURATION;
  }

  function dispose() {
    group.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
        const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
        for (const material of materials) {
          (material as THREE.MeshLambertMaterial).map?.dispose();
          material.dispose();
        }
      }
    });
  }

  return { group, update, swingArm, dispose };
}

/** Deterministic outfit from a string id (remote players). */
export function colorsFromId(id: string): PlayerModelColors {
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = (hash * 31 + id.charCodeAt(i)) | 0;
  }
  const hue = ((hash % 360) + 360) % 360;
  const hue2 = (hue + 160) % 360;
  const toHex = (h: number, s: number, l: number) =>
    `#${new THREE.Color().setHSL(h / 360, s, l).getHexString()}`;
  return {
    ...DEFAULT_COLORS,
    shirt: toHex(hue, 0.6, 0.45),
    pants: toHex(hue2, 0.45, 0.35),
  };
}

/** Outfit themed by champion class color. */
export function colorsFromClassColor(classColor: string): PlayerModelColors {
  return {
    ...DEFAULT_COLORS,
    shirt: classColor,
    pants: "#2a2a3a",
  };
}
